import { Injectable, computed, inject, signal } from '@angular/core';
import { VariablesService } from '../variables/variables.service';
import { ApiService } from '../api/api.service';
import { UtilService } from '../util/util.service';
import { ICartParams, IContributeParams, IOrderParams, IPaymentOptions, ICart, ICartCampaign, IContribute, ISendParams, ISendResponse, IPaymentConfig, NEW_PAYMENT_DEFAULT_CONFIG } from '../../model/payment.model';
import { API_URLS } from 'src/environments/api-urls';
import { ABService } from '../AB/ab.service';
import { EventsService } from '../events/events.service';
import { AuthService } from '../auth/auth.service';
import { IUser } from '../../model/user.model';
import { IOrder } from '../../model/order.model';
import { IFundraiser } from '../../model/fundraiser.model';
import { ISipCause } from '../../model/sip-cause.model';
import { Currency } from '../../model/currency-list';
import { ScriptLoaderService } from '../script-loader/script-loader.service';
import { StorageService } from '@services/storage/storage.service';

declare const Stripe: any;

@Injectable({
  providedIn: 'root'
})
export class PaymentService {
  private AB = inject(ABService);
  private api = inject(ApiService);
  private auth = inject(AuthService);
  private events = inject(EventsService);
  private vars = inject(VariablesService);
  private util = inject(UtilService);
  private scriptLoader = inject(ScriptLoaderService);
  private readonly storage = inject(StorageService);


  captchaResponse?: string;
  cartParams?: ICartParams;
  cart?: ICart;
  failedCart?: ICart;
  cartCampaign?: ICartCampaign;
  isUpiMode: boolean = false;
  orderParams?: IOrderParams;
  orderRes?: IOrder;
  sendApiParams?: ISendParams;
  sendApiRes?: ISendResponse;
  skipRedirectAfterPayment = false;
  paymentStartedFrom = '';
  sipDonationPeriod = 3;
  otdPaymentGateway = '';
  sipPaymentGateway = '';
  paypalCardFields: any;
  errorInPaymentApi: 'cart' | 'order' | 'send' | '' = '';

  readonly qParams = computed(() => this.vars.queryParams());
  readonly formConfig = signal<IPaymentConfig>(NEW_PAYMENT_DEFAULT_CONFIG);
  readonly supportedCheckouts = ['standard', 'custom'];
  readonly PaypalNotSupportedCurrency = ['INR', 'AED', 'SAR', 'QAR', 'MYR'];
  readonly hideSlab = signal<boolean>(false)
  readonly isSip = signal<boolean>(false);
  readonly isSipThrice = signal<boolean>(false);
  readonly checkoutType = signal<'standard' | 'custom'>('custom');
  readonly contributeRes = signal<IContribute | undefined>(undefined);
  readonly contributeSipRes = signal<IContribute | undefined>(undefined);
  readonly optionsRes = signal<IPaymentOptions | undefined>(undefined);
  readonly optionsSipRes = signal<IPaymentOptions | undefined>(undefined);
  readonly selectedAmount = signal<number>(0);
  readonly tipAmountPercent = signal<number>(0);
  readonly tipAmount = signal<number>(0);
  readonly tipType = signal<'manual' | 'percent'>('percent');
  readonly showTxnCost = signal(false);
  readonly isTxnCostSelected = signal(false);
  readonly txnCost = signal<number>(0);
  readonly currency = signal<string>('');
  readonly selectedSips = signal<ISipCause[]>([]);
  readonly totalSelectedSipsAmount = computed(() => this.selectedSips().reduce((sum, item) => sum + (item.amount || 0), 0) || 0);
  readonly totalCartAmount = computed(() => (this.isSip() ? this.totalSelectedSipsAmount() : this.selectedAmount()) + this.tipAmount() + this.txnCost());
  readonly isLoading = signal<boolean>(false);
  readonly disablePayBtn = signal<boolean>(false);
  readonly isBulkDonation = signal<boolean>(false);
  readonly selectedBulkDonation = signal<{ [key: string]: { amount: number } }>({});
  readonly isPaypalOtd = signal(false);
  readonly isPaypalSip = signal(false);
  readonly onSendRes = signal<any>(null);

  private readonly contributeCache = new Map<string, IContribute>();
  private readonly contributeWaitingUrls = new Map<string, boolean>();

  isContributeInitEventHitFor: number[] = [];

  constructor() {
    this.resetPayment();
  }

  resetPayment() {
    this.cartParams = undefined;
    this.cart = undefined;
    this.cartCampaign = undefined;

    this.orderParams = undefined;
    this.orderRes = undefined;

    this.sendApiParams = undefined;
    this.sendApiRes = undefined;

    this.captchaResponse = undefined;

    this.formConfig.set(NEW_PAYMENT_DEFAULT_CONFIG);
    this.checkoutType.set('custom');
    this.selectedAmount.set(0);
    this.tipAmountPercent.set(0);
    this.tipAmount.set(0);
    this.tipType.set('percent');
    this.showTxnCost.set(false);
    this.isTxnCostSelected.set(false);
    this.txnCost.set(0);
    this.selectedSips.set([]);
    this.isBulkDonation.set(false);
    this.selectedBulkDonation.set({});
    this.disablePayBtn.set(false);
    this.isPaypalOtd.set(false);
    this.isPaypalSip.set(false);
    this.onSendRes.set(null);
    this.hideSlab.set(false);
    this.isSip.set(false);
    this.isSipThrice.set(false);
    this.contributeRes.set(undefined);
    this.contributeSipRes.set(undefined);
    this.optionsRes.set(undefined);
    this.optionsSipRes.set(undefined);
    this.isLoading.set(false);
    this.failedCart = undefined;
    this.errorInPaymentApi = '';
    this.isContributeInitEventHitFor = [];
    this.isUpiMode = false;
    this.paymentStartedFrom = '';
    this.sipDonationPeriod = 3;
    this.otdPaymentGateway = '';
    this.sipPaymentGateway = '';
    this.paypalCardFields = undefined;
  }

  getContributeData(data: IContributeParams, isSip = false) {
    return new Promise((resolve, reject) => {
      try {
        const { currency, campaign_id, sip_cause } = data;
        if (!currency || (!isSip && !campaign_id) || (isSip && !sip_cause)) return reject(false);
        let ePayload: any = {
          ...data,
          device: this.vars.deviceType(),
          origin: this.vars.origin(),
          paypal_ab: this.isSipThrice() ? 0 : this.AB.pg_paypal_AB() ? 1 : 0,
          skipmin: this.qParams()?.skipmin ? true : undefined,
          slab_ab: this.AB.slab_AB() || null,
          ...(!isSip ? {
            no_pan_change: true,
            update_multiplier: true,
            is_story: this.vars.origin() === 's' ? true : null,
            pcurrency: data?.previousCurrency,
          } : {})
        };
        delete ePayload?.previousCurrency;
        delete ePayload?.checkout_type;

        const basePath = isSip ? API_URLS.GET_CONTRIBUTE_SIP() : API_URLS.GET_CONTRIBUTE_OTD(data?.campaign_id);
        const url = `${basePath}?${this.util.objectToUrlString(ePayload)}`;
        if (this.contributeWaitingUrls.get(url)) return resolve(undefined);
        this.contributeWaitingUrls.set(url, true);
        if (this.contributeCache.has(url)) {
          const cached = this.contributeCache.get(url)!;
          if (!this.hideSlab()) this.selectedAmount.set(0);
          if (isSip) {
            this.contributeSipRes.set(cached);
          } else {
            this.contributeRes.set(cached);
          }
          this.contributeWaitingUrls.set(url, false);
          return resolve(cached);
        }
        this.vars.showSkeletonFor.update(v => ({ ...v, slab: true }));
        this.isLoading.set(true);

        this.api.get(url).subscribe({
          next: (res: any) => {
            if (!res?.data?.data) { this.isLoading.set(false); reject(false); return; }
            if (typeof res.data.data.default_amount === 'string') res.data.data.default_amount = Number(res.data.data.default_amount);
            if (typeof res.data.data.min_donation === 'string') res.data.data.min_donation = Number(res.data.data.min_donation);
            res.data.data.slabs = (res?.data?.data?.slabs ?? []).map((v: any) => Number(v)).filter((v: any) => !Number.isNaN(v));
            this.contributeWaitingUrls.set(url, false);
            const resData: IContribute = res.data.data;
            resData['timestamp'] = new Date().getTime();
            resData['selected_currency'] ??= data.currency;
            try {
              if (this.qParams()?.min_donation && this.vars.numberOnlyRegex.test(this.qParams()?.min_donation)) {
                resData['min_donation'] = +this.qParams()?.min_donation;
              }
            } catch (error) { }
            let checkout_type = data?.checkout_type || res?.data?.data?.checkout_type;
            if (this.supportedCheckouts.includes(checkout_type)) {
              this.checkoutType.set(checkout_type);
            }
            if (!this.hideSlab()) this.selectedAmount.set(0);
            if (isSip) {
              this.sipPaymentGateway = resData?.payment_methods?.payment_gateway || '';
              this.isPaypalSip.set(this.sipPaymentGateway?.match('paypal') ? true : false);
              this.contributeSipRes.set(resData);
            } else {
              this.otdPaymentGateway = resData?.payment_methods?.payment_gateway || '';
              this.isPaypalOtd.set(this.otdPaymentGateway?.match('paypal') ? true : false);
              this.contributeRes.set(resData);
            }
            this.vars.supportedCurrency.set(this.vars.currencies.filter(val => resData?.supported_currency.includes(val.currency)));
            this.contributeCache.set(url, resData);
            // if (!this.isContributeInitEventHitFor.includes(resData?.campaign?.id || this.vars.fundraiser()?.id || 0)) {
            //   this.eventForInitiatePayment(resData?.campaign || this.vars.fundraiser() || {}, 'contribute_api', isSip, ePayload?.sip_cause || '');
            // }
            this.sendDonationSlabEvent();
            this.isLoading.set(false);
            this.vars.showSkeletonFor.update(v => ({ ...v, slab: false }));
            resolve(resData);
          },
          error: (err: any) => {
            this.contributeWaitingUrls.set(url, false);
            this.isLoading.set(false);
            this.vars.showSkeletonFor.update(v => ({ ...v, slab: false }));
            this.util.openSnackBar(err?.error?.message, 'error');
            reject(false);
          }
        });
      } catch (error) {
        this.isLoading.set(false);
        reject(false);
      }
    });

  }


  getOptionsDetails(data?: { currency?: string, amount?: number, mobile_no?: number, is_sip?: 1 | 0 }) {
    return new Promise((resolve, reject) => {
      try {
        if (!data?.currency) { reject(false); return; }

        if (this.checkoutType() === 'standard') {
          const options = {
            gateway: 'eqall'
          };
          this.optionsRes.set(options);
          resolve(options);
          return;
        }

        const userData = this.vars.userData();

        const optionsPayload = {
          campaign_id: this.cart?.campaign_id,
          currency: data?.currency,
          amount: data?.amount || this.cart?.donated_amount,
          mobile_no: data?.mobile_no || this.cart?.donor_phone || userData?.phone_1,
          recurring: data?.is_sip || this.cart?.recurring || undefined,
          is_mobile: this.vars.isMobile() ? 1 : 0,
          mode: this.isUpiMode ? 'UPI' : undefined,
          // indian: this.cart?.indian === true ? true : false
        }

        const path = API_URLS.GET_PAYMENT_OPTIONS + '?' + this.util.objectToUrlString(optionsPayload);
        this.isLoading.set(true);
        this.api.get(path).subscribe({
          next: (res: any) => {
            this.isLoading.set(false);
            this.optionsRes.set(res?.data);
            resolve(res?.data);
          },
          error: (err: any) => {
            this.isLoading.set(false);
            this.util.openSnackBar(err?.error?.message, 'error');
            reject(false);
          }
        });
      } catch (error) {
        reject(false);
      }
    });
  }

  createCart(data: ICartParams) {
    return new Promise((resolve, reject) => {
      try {

        // if (!data?.campaign_id || !data?.currency || !data?.donated_amount || !data?.donor_name || !data?.donor_email || !data?.donor_extension || !data?.donor_phone) {
        //   reject(false);
        //   return;
        // }
        if (!data?.campaign_id || !data?.currency || !data?.donated_amount) {
          reject(false);
          return;
        }

        if (data?.donor_email && data?.donor_email?.match(this.vars.guestEmailExtn)) {
          this.vars.isGuestUser.set(true);
        } else {
          this.vars.isGuestUser.set(false);
        }

        const clientLocationData = this.vars.clientLocationData();
        const payload: any = {
          ...data,
          'donated_amount': data?.bulkAmount || data?.donated_amount,
          'donor_phone': data.donor_phone?.toString()?.replace(/\s/g, ''),
          'is_anonymous': data?.is_anonymous || '0', // '0' or '1'
          'donor_country': clientLocationData?.country_name,
          'donor_city': clientLocationData?.city_name,
          'g-recaptcha-response': this.captchaResponse || undefined,
          'device': this.vars.deviceType()
        };

        if (data?.recurring) {
          payload.cause_amount = 1;
          payload.split = false;
        }

        delete payload?.bulkAmount;

        const cartPayload = this.util.removeEmptyFromObject(payload);

        this.isLoading.set(true);

        const path = API_URLS.CREATE_CART(data?.campaign_id);
        this.api.post(path, cartPayload).subscribe({
          next: async (res: any) => {
            this.errorInPaymentApi = '';
            this.captchaResponse = '';
            if (!res?.data?.cart) { this.isLoading.set(false); reject(false); return; }

            if (this.vars.isDummyEmail() || (this.vars.isGuestUser() && payload?.donor_email)) {
              this.events.updateCTprofile({ name: payload?.donor_name, email: payload?.donor_email });
            }

            const cartData = {
              ...res.data,
              timestamp: new Date().getTime()
            }
            this.vars.userPhoneExt.set(`${cartData.cart.donor_extension}`);
            this.vars.userPhoneNumber.set(`${cartData.cart.donor_phone}`);
            const userData = this.vars.userData();
            if (userData && !userData?.phone_1) {
              userData.extension = this.vars.userPhoneExt();
              userData.phone_1 = this.vars.userPhoneNumber();
              this.util.setUserData(userData);
            }

            if (this.contributeRes()?.ask_citizenship) {
              cartData.cart.indian = this.vars.isIndian() || '0';
            }

            if (cartData?.token && !this.vars.authToken()) {
              this.vars.loginType.set(!cartData?.login ? 'signin' : 'signup');
              this.vars.loginMethod.set('cart');
              await this.auth.userLogin({ user: cartData.user, token: cartData.token, login: cartData.login });
            }

            this.cart = cartData?.cart;
            this.cartCampaign = cartData?.campaign;

            this.cart = {
              ...this.cart,
              tip_amount: data?.tip_amount || undefined,
              tip_amount_perc: data?.tip_amount_perc || undefined
            }

            const storeCartRes = {
              cart: this.cart,
              campaign: this.cartCampaign,
              timestamp: new Date().getTime()
            }

            if (this.cart.recurring) {
              this.cart.sip_cause_str = cartData.cart.sip_cause_str = this.util.jsonToString(data?.sip_cause);
              this.util.storage.checkFromSession('sipCartData', storeCartRes);
            } else {
              this.util.storage.checkFromSession('cartData', storeCartRes);
            }
            if (!this.isContributeInitEventHitFor.includes(this.cartCampaign?.id || this.vars.fundraiser()?.id || 0)) {
              this.eventForInitiatePayment(cartData?.campaign, 'cart', this.cart?.recurring, this.cart?.sip_cause_str || '');
            }
            this.eventAfterCreatingCart();

            this.isLoading.set(false);
            resolve(cartData);
          },
          error: (err: any) => {
            this.errorInPaymentApi = 'cart';
            this.isLoading.set(false);
            this.util.openSnackBar(err?.error?.message, 'error');
            if (this.isSip() && this.formConfig().showOtdSipSwitch && err?.error?.message?.match(`Looks like you're already giving monthly`)) {
              this.isSip.set(false);
              this.formConfig.update(v => ({ ...v, showOtdSipSwitch: false }));
              this.isSipThrice.set(false);
            }
            reject(false);
          }
        });

      } catch (error) {
        reject(false);
      }
    });
  }

  createOrder(data?: IOrderParams) {
    return new Promise((resolve, reject) => {
      if (data?.customer_id) {
        this.sendApiParams = { ...this.sendApiParams, customer_id: data?.customer_id };
      }

      data = {
        ...data,
        campaign_id: data?.campaign_id || this.cart?.campaign_id,
        cart_id: this.cart?.id,
        gateway: this.optionsRes()?.gateway,
        payment_gateway: this.cart?.recurring ? this.sipPaymentGateway : this.otdPaymentGateway,
        bin: data?.mode === 'CARD' && !data?.customer_id && data?.card_number_fill ? data?.card_number_fill?.substring(0, 6) : null,
        page_type: this.vars.pageName(),
        vpa: data?.VPA,
        is_indian: this.cart?.indian,
        is_multi_patient_campaign: this.vars.donateMultiPatient() || undefined,
        support_campaign_id: this.vars.fundraiser()?.support_campaign?.id,
        checkout_type: data?.checkout_type || this.checkoutType(),
        cause_amount: 1,
        paypal_ab: this.isSipThrice() ? 0 : this.AB.pg_paypal_AB() ? 1 : 0,
      };
      this.orderParams = { ...data };

      delete this.orderParams?.card_number_fill;
      delete this.orderParams?.card_name_fill;
      delete this.orderParams?.card_month_fill;
      delete this.orderParams?.card_year_fill;
      delete this.orderParams?.card_cvv_fill;
      delete this.orderParams?.VPA;
      delete this.orderParams?.customer_id;

      if (this.orderParams?.vpa && this.vars.recommendedVpa() && this.orderParams?.vpa !== this.vars.recommendedVpa()) {
        this.events.sendSystemEvent({
          eventName: 'vpa_changed',
          info_1: 'new vpa = ' + this.orderParams?.vpa,
          info_2: 'old vpa = ' + this.vars.recommendedVpa()
        });
      }
      if (this.showTxnCost() && this.isTxnCostSelected()) {
        this.orderParams = { ...this.orderParams, cover_txn_cost: 1 }
      }
      const orderPayload = this.util.removeEmptyFromObject(this.orderParams);

      this.isLoading.set(true);
      const path = API_URLS.CREATE_ORDER(this.cart?.campaign_id || this.vars.fundraiser()?.id);
      this.api.post(path, orderPayload).subscribe({
        next: async (res: any) => {
          this.errorInPaymentApi = '';
          this.orderRes = res?.data;
          this.eventAfterCreatingOrder();
          this.util.storage.checkFromSession(this.orderRes?.order_id || '', this.orderRes);
          if (data?.checkout_type === 'standard' || data?.mode === 'LINK') {
            this.isLoading.set(false);
            resolve(await this.sendOrder());
          } else {
            if (this.orderRes?.payment_gateway?.match('paypal')) {
              resolve(await this.sendOrder());
            } else if (this.orderRes?.payment_gateway?.match('stripe') && !data?.oneTapPay) {
              this.stripeCustomCheckout(data);
            } else if (data?.oneTapPay) {
              resolve(await this.sendOrder());
            }
          }
        },
        error: (err: any) => {
          this.errorInPaymentApi = 'order';
          this.isLoading.set(false);
          this.util.openSnackBar(err?.error?.message, 'error');
          reject(false);
        }
      });
    });
  }

  async stripeCustomCheckout(data?: IOrderParams) {
    await this.scriptLoader.loadScript('stripe');
    if (typeof Stripe === 'undefined') {
      this.stripeCustomCheckout(data);
      return;
    }

    if (this.currency() !== 'INR' && data?.customer_id) {
    } else if (this.optionsRes()?.settings?.stripe?.key) {
      this.getStripeToken(data);
    } else {
      this.isLoading.set(false);
      this.util.openSnackBar('Something went wrong please try again', 'error');
    }
  }

  async getStripeToken(data?: IOrderParams) {
    Stripe.setPublishableKey(this.optionsRes()?.settings?.stripe?.key);
    try {
      const token = await this.createStripeCardToken(data);
      this.sendApiParams = { ...this.sendApiParams, token: token };
      this.sendOrder();
    } catch (err: any) {
      this.util.openSnackBar(err?.message, 'error');
      this.isLoading.set(false);
    }
  }

  createStripeCardToken(data?: IOrderParams) {
    return new Promise((resolve, reject) => {
      try {
        const stipePaylad = {
          number: data?.card_number_fill,
          cvc: data?.card_cvv_fill,
          exp_month: data?.card_month_fill,
          exp_year: data?.card_year_fill
        };
        Stripe.card.createToken(stipePaylad, (status: any, response: any) => response?.error ? reject(response.error) : resolve(response.id));
      } catch (error) {
        reject(false);
      }
    });
  }

  sendOrder() {
    return new Promise((resolve, reject) => {
      try {
        const userData = this.vars.userData();
        this.vars.tyParams = {
          ...this.qParams(),
          id: this.orderRes?.campaign_id,
          or: this.vars.origin(),
          dnr: userData?.aggdonationall?.funds ? 'rp' : 'fs',
          lpm: this.orderRes?.payment_gateway?.match('stripe') ? 'strp' : 'nstrp',
          url: this.util.router.url.split('?')[0],
          donated_currency: this.orderRes?.iso_currency,
          donated_amount: this.orderRes?.donated_amount,
          tip_amount: this.orderRes?.tip_amount || null,
          amt: this.cart?.recurring ? this.orderRes?.donated_amount : null,
          ct: this.cartCampaign?.custom_tag,
          oi: this.orderRes?.order_id,
          u_or: this.qParams()?.or,
          sub_campaign_id: this.orderRes?.sub_campaign_id,
          sip_cause: this.cart?.recurring && this.selectedSips() ? this.util.jsonToString(this.selectedSips()) : null
        };
        if (!this.vars.tyParams) { reject(true); return; }

        if (this.vars.fundraiser()?.is_multi_patient_campaign) {
          this.vars.tyParams.multi_patient_opted = this.vars.donateMultiPatient() || 0;
        }
        if (userData?.lastorder?.campaign?.custom_tag) {
          this.vars.tyParams.precamp = `${this.qParams()?.or === 's' ? 'stories' : 'fundraiser'}/${userData?.lastorder?.campaign?.custom_tag}`;
        }

        if (this.isSipThrice()) {
          this.vars.tyParams.precamp = `stories/${this.vars?.fundraiser()?.custom_tag}`;
        }

        delete this.vars.tyParams?.page;
        delete this.vars.tyParams?.st;
        delete this.vars.tyParams?.last_success_order_id;
        delete this.vars.tyParams?.payment;
        delete this.vars.tyParams?.method;
        delete this.vars.tyParams?.method_type;

        this.vars.tyParams = this.util.removeEmptyFromObject(this.vars.tyParams);
        this.util.storage.addSessionData('payment_return_params', { last_url: this.util.router.url, p_params: JSON.stringify(this.vars.tyParams), ...this.vars.tyParams });

        if (this.vars.tyParams?.sip_cause && this.sipPaymentGateway.match('paypal')) {
          delete this.vars.tyParams?.sip_cause;
        }

        this.sendApiParams = {
          ...this.sendApiParams,
          order_id: this.orderRes?.order_id,
          return_url: `${this.vars.domainData().fullUrl}/payment-route?${this.util.objectToUrlString(this.vars.tyParams)}`,
          sipDonationPeriod: this.isSipThrice() ? this.sipDonationPeriod : undefined,
        };

        this.isLoading.set(true);
        this.api.post(API_URLS.SEND_ORDER, this.sendApiParams).subscribe({
          next: (res: any) => {
            this.errorInPaymentApi = '';
            this.sendApiRes = res?.data;
            if (!this.sendApiRes) { reject(false); return; }

            if (res?.data?.charged && res?.data?.status === 'success') {
              if (!this.skipRedirectAfterPayment) this.goToThankYouPage();
              this.isLoading.set(false);
              resolve(true);
              return;
            }

            if (res?.data?.charged === false && this.sendApiRes?.params?.client_secret) {
              this.isLoading.set(false);
              return;
            }

            if (res?.data?.charged === false) {
              if (this.vars.isBrowser() && this.sendApiRes?.redirect_url && this.orderRes?.payment_gateway === 'paypal_recurr') {
                document.location.href = this.sendApiRes?.redirect_url;
                return;
              }
              if (this.orderRes?.payment_gateway === 'paypal') {
                this.onSendRes.update(v => this.sendApiRes);
                resolve(true);
                return;
              }
              this.redircetToPaymentGateway();
            }
          },
          error: (err: any) => {
            this.errorInPaymentApi = 'cart';
            this.isLoading.set(false);
            this.util.openSnackBar(err?.error?.message, 'error');
            this.sendPaymentFailureEvent(err?.error?.message);
            reject(false);
          }
        });
      } catch (error) {
        reject(false);
      }
    });
  }

  goToThankYouPage() {
    this.util.storage.deleteFromSession('sipCartData');
    const qparam = {
      oi: this.vars.tyParams?.oi,
      id: this.vars.tyParams?.id,
      or: this.vars.tyParams?.or,
      sip_cause: this.vars.tyParams?.sip_cause || null,
      precamp: this.vars.tyParams?.precamp || null,
      ...this.vars.utm_url_obj()
    }
    this.util.router.navigate(['/thankyou'], { queryParams: qparam }).then(() => {
      // this.util.dialog.closeAll();
      // this.util.sheet.dismiss();
      this.util.storage.deleteFromSession(this.orderRes?.order_id || '');
    });
  }

  async otdSipAuto(data: { amount: number, currency: string, customer_id: string, email: string, ext: string, name: string, or: string, phone: string, sipCotributeData: IContribute }): Promise<any> {
    const sipCause = [{ sip_cause: data.sipCotributeData.sip_cause, amount: data.amount }];
    const cartPayload: any = {
      sip_cause: sipCause,
      campaign_id: data.sipCotributeData.campaign?.id,
      currency: data.currency,
      donated_amount: data.amount,
      donor_email: data.email,
      donor_extension: data.ext,
      donor_name: data.name,
      donor_phone: data.phone,
      recurring: 1
    };
    const sipCart = await this.createCart(cartPayload);

    await this.getOptionsDetails({ currency: data.currency, is_sip: 1 })
    const orderPayload: any = {
      mode: 'CARD',
      mode_value: 'VISA',
      customer_id: data.customer_id,
      sip_cause: sipCause
    }
    this.checkoutType.set('standard');
    this.skipRedirectAfterPayment = true;
    await this.createOrder(orderPayload);
    this.skipRedirectAfterPayment = false;
    return Promise.resolve(this.sendApiRes?.charged ? this.orderRes : false);
  }


  redircetToPaymentGateway() {
    const orderParams: any = { ... this.orderParams };
    let redirectParams: any;
    if (this.sendApiRes?.params) {
      redirectParams = { ...this.sendApiRes?.params };
      Object.entries(this.sendApiRes.params).forEach((val: any, key) => {
        if (orderParams?.hasOwnProperty(val)) {
          redirectParams[key] = orderParams[val]?.toString();
        }
      });
    }

    const frm = document.createElement('form');
    frm.setAttribute('method', this.sendApiRes?.method || '');
    frm.setAttribute('action', this.sendApiRes?.url || '');
    frm.setAttribute('id', 'payment_form');
    frm.setAttribute('class', 'juspay_inline_form'); // Class for juspay payment gateway

    if (redirectParams) {
      for (const key of Object.keys(redirectParams)) {
        const value = redirectParams[key];
        const field = document.createElement('input');
        field.setAttribute('type', 'hidden');
        field.setAttribute('name', key);
        field.setAttribute('class', key);
        field.setAttribute('value', value);
        frm.appendChild(field);
      }
    }
    document.body.appendChild(frm);
    frm.submit();
    setTimeout(() => {
      this.isLoading.set(false);
    }, 2000);
  }

  getPaymentStatus(data: any) {
    return this.api.post(API_URLS.GET_PAYMENT_STATUS(data?.oi), data);
  }

  addCriticalIllness(entity_id: number, order_id: string, selectedIllness: { person: number, cover: string, illnessAmount: number }) {
    const payload = {
      info_1: JSON.stringify({
        person: selectedIllness?.person,
        cover: selectedIllness?.cover,
        add_on_price: selectedIllness?.illnessAmount
      }),
      info_2: order_id,
      info_type: 'critical_illness'
    };
    this.api.put(API_URLS.ADD_CRITICAL_ILLNESS(entity_id), payload).subscribe({
      next: (resp) => {
      }
    });
  }

  getSlabObject(slabs: number[]) {
    return (slabs || []).reduce((acc, val, idx) => {
      acc[`Slab ${idx + 1} value`] = val;
      return acc;
    }, {} as Record<string, number>) || {};
  }

  //send events on payment failure
  sendPaymentFailureEvent(paymentError?: string) {
    const systemEvent = {
      eventName: 'payment_failure_popup',
      event_type: this.cart?.recurring ? 'sip' : 'campaign',
      event_type_id: this.orderRes?.campaign_id,
      info_1: paymentError ?? ''
    };
    this.events.sendSystemEvent(systemEvent);
  }

  sendDonationSlabEvent() {
    const isSip = this.isSip();
    const contributeData = isSip ? this.contributeSipRes() : this.contributeRes();
    if (contributeData) {
      const systemEvent = {
        eventName: 'Donation_Slabs',
        event_type: isSip ? 'sip' : 'campaign',
        info_1: JSON.stringify(contributeData?.slabs),
        info_2: isSip ? this.contributeSipRes()?.sip_cause : '',
        event_type_id: contributeData?.campaign?.id,
        ...this.getSlabObject(contributeData?.slabs),
        ...(isSip ? { sip_cause: this.contributeSipRes()?.sip_cause } : {})
      };
      this.events.sendSystemEvent(systemEvent);
    }
  }

  eventForInitiatePayment(fundraiser: IFundraiser, position = '11', isSip = this.isSip(), sip_cause = '') {
    return new Promise((resolve, reject) => {
      try {
        const { id: campaign_id, custom_tag, title, cause_id, parent_cause_id, leaderboard } = fundraiser;
        if (campaign_id) this.isContributeInitEventHitFor.push(campaign_id || 1);
        let sipCause = this.selectedSips();
        if (!sipCause.length && this.cart?.sip_cause_str) {
          sipCause = JSON.parse(this.cart?.sip_cause_str);
        }
        const only_cause_list = sipCause ? this.totalSip(sipCause).only_cause_list : [];
        const claverTapProperty: any = {
          'Position': position,
          'Custom Tag': custom_tag,
          'Recurring': isSip,
          'Campaign Title': title,
          'Campaign image URL': leaderboard?.cdn_path || '',
        };
        if (this.cart) claverTapProperty['Cart ID'] = this.cart.id;
        if (isSip) claverTapProperty['SIP Acquisition page name'] = this.util.toTitleCase(only_cause_list) + ' SIP';
        const alreadyDonated = this.storage.getFromSession(custom_tag || '');
        if (alreadyDonated) claverTapProperty['donation_completed_same_session'] = true;

        this.events.claverTapPush('Contribution Initiated', claverTapProperty);
        const dataLayer = {
          'event': 'Contribution Initiated',
          'event_category': 'Contribution Initiated',
          'event_action': 'click',
          'event_label': title,
          'Campaign_custom_tag': custom_tag,
          'Campaign_cause_id': cause_id,
          'Campaign_id': campaign_id,
          'Campaign_Type': this.util.getCampaignTypeFromId(parent_cause_id),
          'position': position,
          sip: isSip ? true : false
        };
        this.events.gtmPush(dataLayer);

        let info1 = isSip ? sip_cause : 'position';
        if (this.vars.eventInfos.contributeInitiate.info_1) {
          info1 += `,${this.vars.eventInfos.contributeInitiate.info_1}`;
        }
        let info2 = position;
        if (this.vars.eventInfos.contributeInitiate.info_2) {
          info2 += `,${this.vars.eventInfos.contributeInitiate.info_2}`;
        }
        let info3 = '';
        if (this.vars.eventInfos.contributeInitiate.info_3) {
          info3 += `,${this.vars.eventInfos.contributeInitiate.info_3}`;
        }

        const systemEvent: any = {
          eventName: 'Contribution Initiated',
          event_type: isSip ? 'sip' : 'campaign',
          event_type_id: campaign_id,
          info_1: info1,
          info_2: info2,
          info_3: info3,
          position: position,
          ...(isSip ? { sip_cause: sip_cause } : {})
        };
        if (isSip) {
          systemEvent['is_sip'] = 'yes';
          systemEvent['sip_cause'] = this.util.arrayTostring(only_cause_list, ',');
          systemEvent.info_1 = this.util.arrayTostring(only_cause_list, ',');
        }
        this.events.sendSystemEvent(systemEvent);
        resolve(true);
      } catch (error) {
        reject(false);
      }
    });
  }

  eventAfterCreatingCart() {
    return new Promise((resolve, reject) => {
      try {
        const only_cause_list = this.cart?.recurring ? this.totalSip(this.selectedSips()).only_cause_list : [];
        const slabs = this.cart?.recurring ? this.contributeSipRes()?.slabs : this.contributeRes()?.slabs;
        let info1 = slabs?.length ? `${JSON.stringify(slabs)}/${this.cart?.donated_amount}` : '';
        if (this.vars.eventInfos.contributeCart.info_1) {
          info1 = `${info1},${this.vars.eventInfos.contributeCart.info_1}`;
        }
        let info2: any = this.cart?.id;
        if (this.vars.eventInfos.contributeCart.info_2) {
          info2 = `${info2},${this.vars.eventInfos.contributeCart.info_2}`;
        }
        let info3 = this.vars.pageName();
        if (this.vars.eventInfos.contributeCart.info_3) {
          info3 += `,${this.vars.eventInfos.contributeCart.info_3}`;
        }
        if (this.vars.fundraiser()?.is_multi_patient_campaign && this.vars.fundraiser()?.is_multi_patient?.info_1 === '1' && this.vars.pageName().match('stories')) {
          info3 += this.vars.donateMultiPatient() ? ',multi_p_enabled' : ',multi_p_disabled';
        }
        if (this.paymentStartedFrom) {
          info2 = `${info2},${this.paymentStartedFrom}`;
        }
        if (this.vars.selectedKCart()) {
          info1 = this.getSelectedProductAsInfo() as string;
        }

        let systemEvent: any = {
          eventName: 'Contribution Cart',
          event_type: 'campaign',
          event_type_id: this.cart?.campaign_id,
          info_1: info1,
          info_2: info2,
          info_3: info3,
          ...(this.cart?.recurring ? { sip_cause: this.util.arrayTostring(only_cause_list, ',') } : {}),
          ...this.getSlabObject(slabs || []),
          'Cart Value': this.cart?.donated_amount
        };
        if (this.cart?.bulkdonation) {
          const total = Object.entries(this.cart.bulkdonation).filter(([key]: any) => +key !== this.cart?.campaign_id).reduce((sum, [, val]: any) => sum + (val.amount || 0), 0);
          systemEvent['Bulk order Value'] = total;
        }
        if (this.cart?.recurring) {
          systemEvent['is_sip'] = 'yes';
          systemEvent['sip_cause'] = this.util.arrayTostring(only_cause_list, ',');
          systemEvent.info_1 = `${this.util.arrayTostring(only_cause_list, ',')}, ${systemEvent.info_1}`;
        }

        this.events.sendSystemEvent(systemEvent);

        const dataLayerCartCreate = {
          event: 'crto_basketpage',
          currency: this.cart?.currency,
          crto: {
            email: this.cart?.donor_email,
            products: [{
              id: this.cart?.campaign_id,
              price: this.cart?.donated_amount,
              quantity: '1'
            }]
          }
        };
        this.events.gtmPush(dataLayerCartCreate);

        const claverTapProperty: any = {
          'id': this.cart?.campaign_id,
          'Step': 1,
          'Name': this.cart?.donor_name,
          'Email': this.cart?.donor_email,
          'NC Email ID': this.cart?.donor_email,
          'Type_AB': 'typeAB',
          'Custom Tag': this.cartCampaign?.custom_tag || this.vars.fundraiser()?.custom_tag,
          'Parent Cause Id': this.cartCampaign?.parent_cause_id || this.vars.fundraiser()?.parent_cause_id,
          'iso_currency': this.cart?.currency,
          'amount': this.cart?.donated_amount,
          'TipAmount': this.cart?.tip_amount,
          'Cart ID': this.cart?.id,
          'Recurring': this.cart?.recurring ? true : false
        };
        if (this.cart?.reward_id) {
          claverTapProperty['Amount'] = this.cart?.donated_amount_inr;
          claverTapProperty['Reward'] = this.cart?.reward_id;
        }
        if (this.cart?.recurring) {
          claverTapProperty['SIP Acquisition page name'] = this.util.toTitleCase(only_cause_list) + ' SIP';
        }
        this.events.claverTapPush('New Contribution', claverTapProperty);

        const campaignType = `${this.util.getCampaignTypeFromId(this.cartCampaign?.parent_cause_id || this.vars.fundraiser()?.parent_cause_id)}`;
        const gtmCheckout = {
          'event': 'addToCart',
          'sip': this.cart?.recurring,
          'value': this.cart?.donated_amount_inr,
          'currency': this.cart?.currency,
          'ecommerce': {
            'add': {
              'products': [{
                'name': `${this.cartCampaign?.custom_tag || this.vars.fundraiser()?.custom_tag}`,
                'id': `${this.cart?.campaign_id || this.vars.fundraiser()?.id}`,
                'category': campaignType,
                'variant': `${this.cartCampaign?.cause_id || this.vars.fundraiser()?.cause_id}`,
                'price': this.cart?.donated_amount_inr,
                'quantity': 1,
                'dimension5': '',
                'dimension6': this.cart?.currency,
                'dimension7': `${this.cartCampaign?.cause_id || this.vars.fundraiser()?.cause_id}`,
                'dimension8': `${this.cartCampaign?.custom_tag || this.vars.fundraiser()?.custom_tag}`,
                'dimension9': `${this.cart?.campaign_id || this.vars.fundraiser()?.id}`,
                'dimension10': campaignType,
                'dimension15': this.cart?.donated_amount_inr,
                'dimension16': this.tipAmountPercent() || '',
                'dimension17': this.cart?.tip_amount_inr,
              }]
            }
          }
        };
        this.events.gtmPush(gtmCheckout);
        resolve(true);
      } catch (error) {
        reject(false);
      }
    });
  }

  eventAfterCreatingOrder() {
    return new Promise((resolve, reject) => {
      try {
        const only_cause_list = this.cart?.recurring ? this.totalSip(this.selectedSips()).only_cause_list : [];
        const claverTap: any = {
          'id': this.cart?.campaign_id,
          'Step': 3,
          'Name': this.cart?.donor_name,
          'Email': this.cart?.donor_email,
          'Amount': this.orderRes?.donated_amount_local,
          'Payment Mode': this.orderRes?.payment_mode,
          'Type_AB': 'typeAB',
          'TipAmount': this.orderRes?.tip_amount_local,
          'Iso Currency': this.orderRes?.iso_currency,
          'Cart ID': this.cart?.id,
          'Recurring': this.cart?.recurring ? true : false
        };
        if (this.vars.fundraiser()) {
          claverTap['Custom Tag'] = this.vars.fundraiser()?.custom_tag;
          claverTap['Parent Cause Id'] = this.vars.fundraiser()?.parent_cause_id;
        }
        if (this.cart?.recurring) {
          claverTap['SIP Acquisition page name'] = this.util.toTitleCase(only_cause_list) + ' SIP';
        }
        this.events.claverTapPush('New Contribution Step 3', claverTap);

        this.checkoutGTMEvent(2);

        const slabs = this.cart?.recurring ? this.contributeSipRes()?.slabs : this.contributeRes()?.slabs;
        let info1 = slabs?.length ? `${JSON.stringify(slabs)}/${this.cart?.donated_amount}` : '';
        if (this.vars.eventInfos.orderCreated.info_1) {
          info1 += `,${this.vars.eventInfos.orderCreated.info_1}`;
        }
        let info2: any = this.orderRes?.id;
        if (this.vars.eventInfos.orderCreated.info_2) {
          info2 += `,${this.vars.eventInfos.orderCreated.info_2}`;
        }
        let info3 = this.vars.pageName();
        if (this.vars.eventInfos.orderCreated.info_3) {
          info3 += `;${this.vars.eventInfos.orderCreated.info_3}`;
        }
        if (this.vars.fundraiser()?.is_multi_patient_campaign && this.vars.fundraiser()?.is_multi_patient?.info_1 === '1' && this.vars.pageName().match('stories')) {
          info3 += this.vars.donateMultiPatient() ? ',multi_p_enabled' : ',multi_p_disabled';
        }

        let systemEvent: any = {
          'eventName': 'order_created',
          'event_type': 'campaign',
          'event_type_id': this.cart?.campaign_id,
          'page_name': this.vars.pageName() || '',
          'info_1': info1,
          'info_2': info2,
          'info_3': info3,
          'Currency': this.cart?.currency,
          'Donated Amount (Local)': this.cart?.donated_amount,
          ...(this.cart?.recurring ? { sip_cause: this.util.arrayTostring(only_cause_list, ',') } : {})
        };

        //for event equalallKart
        if (this.vars.selectedKCart()) {
          systemEvent = {
            ...systemEvent,
            event_type: 'stories',
            event_type_id: this.cart?.campaign_id,
            info_1: this.getSelectedProductAsInfo() as string,
            info_2: this.cart?.donated_amount,
            info_3: 'stories',
          }
        }

        if (this.cart?.recurring) {
          systemEvent['is_sip'] = 'yes';
          systemEvent['sip_cause'] = this.util.arrayTostring(only_cause_list, ',');
          systemEvent.info_3 += `; ${this.util.arrayTostring(only_cause_list, ',')}`;
        }
        this.events.sendSystemEvent(systemEvent);

        resolve(true);
      } catch (error) {
        reject(false);
      }
    });
  }

  checkoutGTMEvent(step: number) {
    const gtmCheckout = {
      'event': 'checkout',
      'sip': this.cart?.recurring ? true : false,
      'value': this.orderRes?.donated_amount,
      'currency': this.orderRes?.iso_currency,
      'ecommerce': {
        'checkout': {
          'actionField': { 'step': step, 'option': this.orderRes?.payment_mode },
          'products': [{
            'name': this.vars.fundraiser()?.custom_tag,
            'id': this.vars.fundraiser()?.id,
            'category': this.util.getCampaignTypeFromId(this.vars.fundraiser()?.parent_cause_id),
            'variant': this.vars.fundraiser()?.cause_id,
            'price': this.orderRes?.donated_amount,
            'quantity': 1,
            'dimension5': '',
            'dimension6': this.orderRes?.iso_currency,
            'dimension7': this.vars.fundraiser()?.cause_id,
            'dimension8': this.vars.fundraiser()?.custom_tag,
            'dimension9': this.vars.fundraiser()?.id,
            'dimension10': this.util.getCampaignTypeFromId(this.vars.fundraiser()?.parent_cause_id),
            'dimension15': this.orderRes?.donated_amount,
            'dimension16': this.tipAmountPercent() || '',
            'dimension17': this.orderRes?.tip_amount_inr
          }]
        }
      }
    };
    this.events.gtmPush(gtmCheckout);
  }

  succesfullOrderEvents(order: IOrder, fundraiser?: IFundraiser) {
    const gtmPurchased = {
      'event': 'purchase',
      'sip': order.subscription_id ? true : false,
      'transaction_id': order.order_id,
      'value': order.donated_amount_usd,
      'currency': order.iso_currency,
      'ecommerce': {
        'purchase': {
          'actionField': {
            'id': order.order_id,
            'revenue': order.donated_amount_usd,
          },
          'products': [{
            'name': fundraiser?.custom_tag,
            'id': fundraiser?.id,
            'category': this.util.getCampaignTypeFromId(fundraiser?.parent_cause_id),
            'variant': fundraiser?.cause_id,
            'price': order.donated_amount_usd,
            'quantity': 1,
            'dimension5': '',
            'dimension7': fundraiser?.cause_id,
            'dimension8': order.donor_first_name,
            'dimension9': fundraiser?.id,
            'dimension10': this.util.getCampaignTypeFromId(fundraiser?.parent_cause_id),
            'dimension15': order.donated_amount_usd,
            'dimension16': this.tipAmountPercent() || '',
            'dimension17': order.tip_amount_local
          }],
          'amount_slab': this.getAmount_slab(order?.donated_amount || 0)
        }
      }
    };
    this.events.gtmPush(gtmPurchased);
  }

  getAmount_slab(value: number) {
    switch (true) {
      case value > 1 && value <= 500:
        return 'Purchase_500';
      case value > 500 && value <= 1000:
        return 'Purchase_1000';
      case value > 1000 && value <= 1500:
        return 'Purchase_1500';
      case value > 1500 && value <= 2000:
        return 'Purchase_2000';
      case value > 2000 && value <= 5000:
        return 'Purchase_5000';
      case value > 5000:
        return 'Purchase_5000plus';
    }
    return null;
  }

  userEventsAfterPayment(order: IOrder, fundraiser?: IFundraiser, userData?: IUser, queryParams?: any) {
    const claverTap: any = {
      'Campaign Name': `${fundraiser?.title}`,
      'Urlencode Campaign Name': encodeURI(fundraiser?.title || ''),
      'Truncated Campaign Name': (fundraiser?.title && fundraiser.title.length > 20) ? fundraiser?.title?.substring(0, 20).concat('...') : fundraiser?.title,
      'Campaign Id': `${fundraiser?.id}`,
      'Amount': `${order.donated_amount_local}`,
      'Reward Id': `${order.rewards_id}`,
      'Charged ID': `${order.order_id}`,
      'Cause ID': `${fundraiser?.cause_id}`,
      'Device Type': this.vars.deviceType(),
      'Page Version': 'Angular',
      'Type_AB': '',
      'Donor Name': order?.donor?.full_name || userData?.full_name,
      'Email ID': order?.donor?.email || userData?.email,
      'Beneficiary Name': fundraiser?.beneficiary?.full_name || '',
      'Parent Cause ID': fundraiser?.parent_cause_id,
      'Description Of Campaign': (fundraiser?.basicInfo?.about && fundraiser?.basicInfo?.about.length > 160) ? fundraiser?.basicInfo?.about?.substring(0, 160).concat('...') : fundraiser?.basicInfo?.about || '',
      'Custom Tag': fundraiser?.custom_tag,
      'ISO Currency': order.iso_currency,
      'Identity': userData?.id || 0,
      'Monthly giving': order.recurring ? 'Yes' : 'No',
      'Tip Added': order?.tip_amount_local ? 'Yes' : 'No',
      'Tip Amount': order?.tip_amount_local || '',
      'Payment mode': order?.payment_mode,
      'NGO ID': fundraiser?.entity_details_id,
      'Event ID': fundraiser?.event_entity_details_id,
      'Update Page URL': `${this.vars.domainData().fullUrl}/campaign/campaign_update?fmd_id=${fundraiser?.id}&email=${userData?.email}&is_donor=1&donated_amount=${order.donated_amount}&donated_currency=${order.iso_currency}&tyvideobtd=true`,
      'Multipatient Page': order?.is_multi_patient_campaign ? 'Yes' : 'No'
    };

    if (order?.is_multi_patient_campaign) {
      claverTap['New Beneficiary Name'] = order?.sub_campaign?.beneficiaryname?.info_1;
      claverTap['Child Campaign Id'] = order?.sub_campaign?.id;
    }

    if (order.transaction_return_status === '200') {
      this.events.claverTapPush('Charged', this.util.removeEmptyFromObject(claverTap));
    }

    if (order.recurring) {
      const recurringOrder: any = {
        'Name': order?.donor?.full_name || userData?.full_name,
        'Amount': order.donated_amount,
        'BeneficiaryName': fundraiser?.beneficiary?.full_name || '',
        'CampaignerName': fundraiser?.campaigner?.full_name,
        'CampaignName': fundraiser?.title,
        'CampaignID': fundraiser?.id,
        'Subscription date': Math.round(new Date(order.recurring.creation_time).getTime() / 1000),
        'SubscriptionMonths': order.recurring.tenure,
        'Payment Mode Type': order.payment_mode,
        'Subscription ID': order.subscription_id,
        'LocalAmount': order.donated_amount_local,
        'LocalCurrency': order.iso_currency,
        // 'Coupon Added': order.offer || false
        'Coupon Added': order.coupon || '',
        'Charge Cause': order?.recurring?.campaign_cause,
        'User Type': queryParams?.utm_source || '',
        'Next Deduction Date': (new Date(order?.recurring?.next_payment_date))?.toLocaleDateString('en-GB')?.split('/')?.join('-'),
        'Next Deduction Amount': order?.recurring?.amount,
        'Donation date': (new Date(order?.creation_date))?.toLocaleDateString('en-GB')?.split('/')?.join('-'),
        'Transaction ID': order?.order_id
      };

      let cause_list = '';
      let amount_list = '';
      if (queryParams?.sip_cause && typeof queryParams?.sip_cause === 'object' && queryParams?.sip_cause?.length > 1) {
        queryParams?.sip_cause.forEach((element: any, i: number) => {
          cause_list = cause_list + `${element?.sip_cause || element}${i < queryParams?.sip_cause?.length - 1 ? ',' : ''}`
          amount_list = amount_list + `${userData?.listsubscriptions?.[element?.sip_cause || element]?.amount}${i < queryParams?.sip_cause?.length - 1 ? ',' : ''}`
        });
        recurringOrder['multi_cause'] = true;
        recurringOrder['cause_list'] = cause_list;
        recurringOrder['amount_list'] = amount_list;
      }

      this.events.claverTapPush('Monthly Giving Impact', this.util.removeEmptyFromObject(recurringOrder));
    }

    const profilePushData: any = {
      'last_charged_amount': order.donated_amount_local,
      'last_charged_tip_amount': order.tip_amount_local,
      'last_charged_beneficiaryname': fundraiser?.beneficiary?.full_name,
      'last_charged_campaignid': fundraiser?.id,
      'last_charged_campaignname': fundraiser?.title,
      'last_charged_orderid': order.order_id,
      'last_charged_customtag': fundraiser?.custom_tag,
      'last_charged_descriptionofcampaign': (fundraiser?.basicInfo?.about && fundraiser?.basicInfo?.about.length > 160) ? fundraiser?.basicInfo?.about?.substring(0, 160).concat('...') : fundraiser?.basicInfo?.about || '',
      'last_charged_identity': userData?.id || 0,
      'last_charged_email_id': userData?.email || '',
      'last_charged_isocurrency': order.iso_currency,
      'last_charged_parentcauseid': fundraiser?.parent_cause_id,
      'last_charged_mobilenumber': userData?.phone_1 || '',
      'SIP Active Donor': this.util.checkForActiveSip(userData?.listsubscriptions) ? 'Yes' : 'No',
    };

    if (!userData?.hasOwnProperty('listsubscriptions')) {
      profilePushData['SIP Active Donor'] = null;
    }

    if ((order.payment_gateway === 'stripe' || order.payment_gateway === 'stripe_india') && !userData?.listsubscriptions?.medical) {
      profilePushData['eligible_for_one_click_activation'] = 'Yes';
    }

    // User attributes using charged events
    this.events.claverProfilePush({ 'Site': this.util.removeEmptyFromObject(profilePushData) });

    // Criteo Sales dataLayer
    const criteoDataLayer = {
      event: 'crto_transactionpage',
      currency: `${order.iso_currency}`,
      crto: {
        email: `${userData?.email || ''}`,
        transactionid: `${order.id}`,
        products: [{
          id: `${fundraiser?.id}`,
          price: `${order.donated_amount_local}`,
          quantity: '1'
        }]
      }
    };
    this.events.gtmPush(criteoDataLayer);
  }

  totalSip(data: ISipCause[]): { total_amount?: number, only_cause_list?: string[], sip_count?: number, is_valid?: boolean } {
    let total = 0;
    let is_valid = true;
    const cause_list: string[] = [];
    if (typeof data === 'string') {
      data = [{ sip_cause: data, amount: 0 }];
    }
    try {
      data.forEach(element => {
        if (element?.sip_cause) {
          total += +element?.amount || 0;
          cause_list.push(element?.sip_cause);
        }
        if (this.contributeSipRes() && this.contributeSipRes()?.min_donation! > +element.amount &&
          (!this.vars?.userData()?.listsubscriptions?.[element?.sip_cause] || this.vars?.userData()?.listsubscriptions?.[element?.sip_cause]?.amount > +element.amount)) {
          is_valid = false;
        }
      });
    } catch (error) { }
    if (cause_list.length === 0 && this.currency()) {
      total = this.getMinSipAmount(this.currency())?.amount;
      cause_list.push(this.vars.defaultSipCause);
    }
    return { total_amount: total, only_cause_list: cause_list, sip_count: cause_list.length, is_valid: is_valid };
  }

  getMinSipAmount(currency: string) {
    const currencies: any = this.vars.minSipAmounts;
    if (currency) {
      currency = currency?.toUpperCase();
      return {
        text: this.util.formatAmount(currencies[currency], currency),
        amount: currencies[currency],
        currency_symbol: Currency.find((item) => item.currency === currency)?.symbol
      };
    }
    return null;
  }

  getSelectedProductAsInfo() {
    let info1 = '';
    if (!this.vars.selectedKCart()) return;
    this.vars.selectedKCart()!.forEach((val, i) => {
      info1 += `${val?.name},${val?.unit_count},${+val?.per_unit_amount * +val?.unit_count}` + (i + 1 < (this.vars.selectedKCart() ?? [])?.length ? ',' : '');
    });
    return info1;
  }
}
